<template>
  <div id="module">
    <v-toolbar id="module-toolbar" absolute ref="toolbar" v-mutate="onMutate">
      <v-toolbar-title>
        <slot name="title">{{ title }}</slot>
      </v-toolbar-title>

      <v-spacer />

      <div v-if="canDelete">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              depressed
              id="delete"
              class="ml-10 button"
              color="error"
              fab
              rounded
              :disabled="disabled"
              @click="initiateDelete"
              data-cy-id="delete-button"
              v-bind="attrs"
              v-on="on"
            >
              <v-icon>pic-delete</v-icon>
            </v-btn>
          </template>
          <span>{{ $t("global.tooltip.effisoftToolbarDelete") }}</span>
        </v-tooltip>
      </div>

      <div v-if="canDeleteOldRuns">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              depressed
              id="delete-old-runs"
              class="ml-10 button"
              color="error"
              fab
              rounded
              :disabled="disabled"
              @click="deleteOldRuns"
              data-cy-id="delete-old-runs-button"
              v-bind="attrs"
              v-on="on"
            >
              <v-icon style="font-size: 23px">mdi-delete-clock</v-icon>
            </v-btn>
          </template>
          <span>{{ $t("global.tooltip.effisoftToolbarDeleteOldRuns") }}</span>
        </v-tooltip>
      </div>

      <div v-if="canClearFilters">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              depressed
              id="clearFilters"
              class="ml-10 button"
              color="error"
              fab
              rounded
              :disabled="disabled"
              @click="clearFilters"
              data-cy-id="clear-filters-button"
              v-bind="attrs"
              v-on="on"
            >
              <v-icon>mdi-filter-remove-outline</v-icon>
            </v-btn>
          </template>
          <span>{{ $t("global.action.clear-filters") }}</span>
        </v-tooltip>
      </div>

      <div v-if="canList">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              depressed
              id="edit"
              class="ml-10 button"
              fab
              rounded
              @click="list($event)"
              data-cy-id="list-button"
              v-bind="attrs"
              v-on="on"
            >
              <v-icon>pic-enumerate</v-icon>
            </v-btn>
          </template>
          <span>{{ $t("global.tooltip.effisoftToolbarList") }}</span>
        </v-tooltip>
      </div>

      <div v-if="canCopy">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              depressed
              id="copy"
              class="ml-10 button"
              fab
              rounded
              @click="copy($event)"
              data-cy-id="copy-button"
              v-bind="attrs"
              v-on="on"
            >
              <v-icon>pic-duplicate</v-icon>
            </v-btn>
          </template>
          <span>{{ $t("global.tooltip.effisoftToolbarCopy") }}</span>
        </v-tooltip>
      </div>

      <div v-if="canImport">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              depressed
              id="import"
              class="ml-10 button"
              fab
              rounded
              @click="importItem($event)"
              data-cy-id="import-button"
              v-bind="attrs"
              v-on="on"
            >
              <v-icon>pic-export</v-icon>
            </v-btn>
          </template>
          <span>{{ $t("global.modules.import") }}</span>
        </v-tooltip>
      </div>

      <div v-if="canDownload">
        <v-tooltip bottom v-if="!isDownloading">
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              depressed
              id="download"
              class="ml-10 button"
              fab
              rounded
              @click="downloadItem($event)"
              data-cy-id="download-button"
              v-bind="attrs"
              v-on="on"
            >
              <v-icon>pic-import</v-icon>
            </v-btn>
          </template>
          <span>{{ downloadTooltip ?? $t("global.action.download") }}</span>
        </v-tooltip>

        <v-progress-circular
          :size="26"
          v-else
          indeterminate
          class="ml-10 mr-1"
        />
      </div>

      <div v-if="canUpload">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              depressed
              id="upload"
              class="ml-10 button"
              fab
              rounded
              @click="uploadItem($event)"
              data-cy-id="upload-button"
              v-bind="attrs"
              v-on="on"
            >
              <v-icon>pic-export</v-icon>
            </v-btn>
          </template>
          <span>{{ $t("global.action.upload") }}</span>
        </v-tooltip>
      </div>

      <div v-if="hasTreeToggle">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              depressed
              id="tree-toggle"
              class="ml-10 button"
              fab
              rounded
              @click="toggleTree($event)"
              data-cy-id="tree-toggle-button"
              v-bind="attrs"
              v-on="on"
            >
              <v-icon v-if="treeEnabled">mdi-book-open</v-icon>
              <v-icon v-else>mdi-book-open-outline</v-icon>
            </v-btn>
          </template>
          <span>{{ treeToggleToolTip }}</span>
        </v-tooltip>
      </div>

      <div v-if="canEdit">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              depressed
              id="edit"
              class="ml-10 button"
              fab
              rounded
              @click="editItem($event)"
              data-cy-id="edit-button"
              v-bind="attrs"
              v-on="on"
            >
              <v-icon>pic-edit</v-icon>
            </v-btn>
          </template>
          <span>{{ $t("global.tooltip.effisoftToolbarEdit") }}</span>
        </v-tooltip>
      </div>

      <div v-if="canCreate">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              depressed
              id="new"
              class="ml-10 button"
              fab
              rounded
              @click="newItem($event)"
              data-cy-id="new-button"
              v-bind="attrs"
              v-on="on"
            >
              <v-icon>pic-add</v-icon>
            </v-btn>
          </template>
          <span>{{ createToolTipComp }}</span>
        </v-tooltip>
      </div>

      <div v-if="canLock">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              depressed
              id="lock"
              class="ml-10 button"
              fab
              rounded
              @click="lock($event)"
              data-cy-id="lock-button"
              v-bind="attrs"
              v-on="on"
            >
              <v-icon>pic-lock</v-icon>
            </v-btn>
          </template>
          <span>{{ $t("global.business-process.lock") }}</span>
        </v-tooltip>
      </div>

      <div v-if="canUnlock">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              depressed
              id="unlock"
              class="ml-10 button"
              fab
              rounded
              @click="unlock($event)"
              data-cy-id="unlock-button"
              v-bind="attrs"
              v-on="on"
            >
              <v-icon>pic-unlock</v-icon>
            </v-btn>
          </template>
          <span>{{ $t("global.business-process.unlock") }}</span>
        </v-tooltip>
      </div>

      <div v-if="canCompareRuns">
        <v-tooltip bottom>
          <template #activator="{ on, attrs }">
            <v-btn
              depressed
              id="compare-runs"
              class="ml-10 button"
              fab
              rounded
              data-cy-id="compare-runs-button"
              v-bind="attrs"
              v-on="on"
              @click="compare($event)"
            >
              <v-icon>mdi-compare-horizontal</v-icon>
            </v-btn></template
          >
          <span> {{ $t("global.run.compare") }}</span>
        </v-tooltip>
      </div>

      <div v-if="canCompareFiles">
        <v-tooltip bottom>
          <template #activator="{ on, attrs }">
            <v-btn
              depressed
              id="compare-files"
              class="ml-10 button"
              fab
              rounded
              data-cy-id="compare-files-button"
              v-bind="attrs"
              v-on="on"
              @click="fileComparisonOn"
            >
              <v-icon>mdi-compare-horizontal</v-icon>
            </v-btn>
          </template>
          <span> {{ $t("global.run.compare") }}</span>
        </v-tooltip>
      </div>

      <file-comparison
        v-if="$store.state.showFileComparison"
        @close="fileComparisonOff"
      >
      </file-comparison>

      <div v-if="$store.state.bpInstReadPermission">
        <v-menu open-on-hover offset-y>
          <template #activator="{ on }">
            <v-btn dark icon v-on="on">
              <v-icon>pic-history</v-icon>
            </v-btn>
          </template>

          <v-list>
            <div v-if="$store.state.recentBps.length > 0">
              <v-list-item
                v-for="bpId in $store.state.recentBps"
                :key="bpId"
                @click="bpClicked(bpId)"
              >
                <v-chip color="orange" dark small> #{{ bpId }} </v-chip>
                <span class="ml-1">
                  {{ $store.state.recentBpsMap[bpId] }}
                </span>
              </v-list-item>
            </div>
            <v-list-item v-else>{{
              $t("global.concepts.no-recent-bps")
            }}</v-list-item>
          </v-list>
        </v-menu>
      </div>

      <account-menu dark-label />
    </v-toolbar>

    <!-- module content -->
    <slot></slot>

    <v-dialog v-model="open" persistent :width="500">
      <v-card>
        <v-card-title>
          <span class="headline">{{ $t("global.action.confirm") }}</span>
        </v-card-title>
        <v-card-text>
          <span>{{ confirmDeleteMessage }}</span>
        </v-card-text>
        <v-card-actions class="justify-end">
          <v-btn
            depressed
            data-cy-id="confirm-delete-button"
            color="error"
            class="is-action"
            @click="
              open = false;
              deleteItem($event);
            "
          >
            {{ $t("global.action.yes") }}
          </v-btn>
          <v-btn depressed class="button" @click="open = false">
            {{ $t("global.action.no") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import AccountMenu from "@/components/layout/AccountMenu.vue";
import FileComparison from "@/components/FileComparison.vue";

export default {
  name: "module-template",

  components: {
    AccountMenu,
    FileComparison,
  },

  props: {
    canCreate: {
      type: Boolean,
      default: false,
    },

    deleteConfirm: {
      type: Boolean,
      default: true,
    },

    canEdit: {
      type: Boolean,
      default: false,
    },

    canLock: {
      type: Boolean,
      default: false,
    },

    canUnlock: {
      type: Boolean,
      default: false,
    },

    canList: {
      type: Boolean,
      default: false,
    },
    canCopy: {
      type: Boolean,
      default: false,
    },

    canCompareRuns: {
      type: Boolean,
      default: false,
    },

    canCompareFiles: {
      type: Boolean,
      default: false,
    },

    canDelete: {
      type: Boolean,
      default: false,
    },

    canDeleteOldRuns: {
      type: Boolean,
      default: false,
    },

    canClearFilters: {
      type: Boolean,
      default: false,
    },

    canSubmit: {
      type: Boolean,
      default: true,
    },

    canImport: {
      type: Boolean,
      default: false,
    },

    canDownload: {
      type: Boolean,
      default: false,
    },

    downloadTooltip: {
      type: String,
      default: undefined,
    },

    canUpload: {
      type: Boolean,
      default: false,
    },

    isDownloading: {
      type: Boolean,
      default: false,
    },

    hasTreeToggle: {
      type: Boolean,
      default: false,
    },

    disabled: {
      type: Boolean,
      default: false,
    },
    deleteMessage: {
      type: String,
      default: null,
    },
    isForm: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      required: true,
    },
    deleteValidation: {
      type: Function,
      required: false,
    },

    createToolTip: {
      type: String,
    },

    treeEnabled: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      open: false,
      dialog: false,

      channel: new BroadcastChannel("show-file-comparison-offset"),
    };
  },

  computed: {
    confirmDeleteMessage() {
      return this.deleteMessage
        ? this.deleteMessage
        : this.$t("global.action.confirm-continue");
    },

    createToolTipComp() {
      return this.createToolTip
        ? this.createToolTip
        : this.$t("global.tooltip.effisoftToolbarAdd");
    },

    recentBps() {
      return [{ value: "download", text: this.$t("global.action.download") }];
    },

    treeToggleToolTip() {
      return this.treeEnabled
        ? this.$t("global.action.close-left-panel")
        : this.$t("global.action.open-left-panel");
    },
  },

  methods: {
    fileComparisonOn() {
      this.channel.postMessage("show");
      this.$store.commit("showFileComparison");
    },

    fileComparisonOff() {
      this.channel.postMessage("hide");
      this.$store.commit("hideFileComparison");
    },

    onMutate() {
      let height = 0;
      const toolbar = this.$refs.toolbar;
      if (toolbar) {
        height = `${toolbar.$el.offsetHeight}px`;
      }
      document.documentElement.style.setProperty("--headerHeight", height);
    },

    bpClicked(bpId) {
      this.$router
        .push({ name: "BP instance", params: { bpId: bpId } })
        .catch((error) => {
          // Don't throw an error when the user navigates to the current route
          if (error.name !== "NavigationDuplicated") {
            throw error;
          }
        });
    },

    validate(event) {
      this.$emit("validate", event);
    },
    saveWithValidate(event) {
      this.$emit("save", event);
    },
    newItem(event) {
      this.$emit("newItem", event);
    },

    editItem(event) {
      this.$emit("editItem", event);
    },

    lock(event) {
      this.$emit("lock", event);
    },

    unlock(event) {
      this.$emit("unlock", event);
    },

    toggleTree(event) {
      this.$emit("toggleTree", event);
    },

    importItem(event) {
      this.$emit("import", event);
    },

    compare(event) {
      this.$emit("compare", event);
    },

    downloadItem(event) {
      this.$emit("download", event);
    },

    uploadItem(event) {
      this.$emit("upload", event);
    },

    list(event) {
      this.$emit("list", event);
    },
    copy(event) {
      this.$emit("copyItem", event);
    },

    deleteItem(event) {
      this.$emit("deleteItem", event);
    },

    cancel(event) {
      this.$emit("cancel", event);
    },

    deleteOldRuns() {
      this.$emit("deleteOldRuns", event);
    },

    initiateDelete() {
      if (!this.deleteConfirm) {
        this.deleteItem();
        return;
      }
      if (this.deleteValidation) {
        this.open = this.deleteValidation();
      } else {
        this.open = true;
      }
    },

    clearFilters() {
      this.$emit("clearFilters", event);
    },
  },

  mounted() {
    this.onMutate();

    this.channel.onmessage = (event) => {
      if (event.data === "show") {
        this.$store.commit("showFileComparison");
      } else if (event.data === "hide") {
        this.$store.commit("hideFileComparison");
      }
    };
  },
};
</script>

<style lang="scss">
#module-toolbar {
  $offset: 24px;
  position: sticky;
  z-index: 7;
  max-width: none;
  margin-left: -$offset;
  margin-top: -$offset;
  width: calc(100% + #{$offset * 2});

  div.v-text-field__slot {
    font-size: 17px;
  }
}
</style>
